import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Camera, X, Plus, Briefcase, MapPin, Check } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileBuilder = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([null, null, null, null, null, null]);
  const [dragOver, setDragOver] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    profession: "",
    company: "",
    location: "",
    prompt1: "", // My next solo escape is to...
    prompt2: "", // Ask me about my expertise in...
    prompt3: "", // My ideal weekend meetup involves...
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (index, file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newPhotos = [...photos];
        newPhotos[index] = e.target.result;
        setPhotos(newPhotos);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = useCallback((index, e) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    handlePhotoUpload(index, file);
  }, []);

  const handleDragOver = (index, e) => {
    e.preventDefault();
    setDragOver(index);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    newPhotos[index] = null;
    setPhotos(newPhotos);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.email) {
          toast.error("Please enter your name and email");
          return false;
        }
        return true;
      case 2:
        const uploadedPhotos = photos.filter(p => p !== null).length;
        if (uploadedPhotos < 1) {
          toast.error("Please upload at least 1 photo");
          return false;
        }
        return true;
      case 3:
        if (!formData.profession || !formData.location) {
          toast.error("Please fill in your profession and location");
          return false;
        }
        if (!formData.prompt1 && !formData.prompt2 && !formData.prompt3) {
          toast.error("Please answer at least one prompt");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        profession: formData.profession,
        company: formData.company,
        location: formData.location,
        photos: photos.filter(p => p !== null),
        prompts: {
          solo_escape: formData.prompt1,
          expertise: formData.prompt2,
          weekend_meetup: formData.prompt3,
        },
      };

      const response = await axios.post(`${API}/profiles/create-premium`, payload);
      
      if (response.data.success) {
        localStorage.setItem("havns_profile_id", response.data.profile_id);
        localStorage.setItem("havns_user", JSON.stringify({
          profile_id: response.data.profile_id,
          name: formData.name,
          email: formData.email,
        }));
        toast.success("Welcome to Havns!");
        navigate("/discover");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Header */}
      <header className="border-b border-[#EEE] bg-[#FBF9F7]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
          <div className="flex items-center gap-2 text-sm text-[#999]">
            Step {currentStep} of 3
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Step 1: Quick Entry */}
        {currentStep === 1 && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h1 className="font-serif text-4xl text-[#1A1A1A] mb-3">Welcome to Havns</h1>
              <p className="text-[#666] text-lg">Let's get you started in 60 seconds</p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Your Name</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="What should we call you?"
                  className="h-14 rounded-2xl border-[#EEE] focus:border-[#1A1A1A] text-lg"
                  data-testid="input-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email</label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="h-14 rounded-2xl border-[#EEE] focus:border-[#1A1A1A] text-lg"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                onClick={() => navigate("/get-started")}
                variant="ghost"
                className="text-[#666] hover:text-[#1A1A1A]"
                data-testid="back-to-getstarted-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={nextStep}
                className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-10 py-7 text-base"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Photo Upload */}
        {currentStep === 2 && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h1 className="font-serif text-4xl text-[#1A1A1A] mb-3">Show your best self</h1>
              <p className="text-[#666]">Add photos that capture your personality</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className={`aspect-[3/4] rounded-2xl overflow-hidden relative group cursor-pointer transition-all ${
                    dragOver === index ? 'ring-2 ring-[#C5B358] scale-[1.02]' : ''
                  } ${photo ? '' : 'bg-[#F5F5F5] border-2 border-dashed border-[#DDD] hover:border-[#999]'}`}
                  onDrop={(e) => handleDrop(index, e)}
                  onDragOver={(e) => handleDragOver(index, e)}
                  onDragLeave={() => setDragOver(null)}
                  onClick={() => {
                    if (!photo) {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => handlePhotoUpload(index, e.target.files[0]);
                      input.click();
                    }
                  }}
                >
                  {photo ? (
                    <>
                      <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removePhoto(index);
                        }}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      {index === 0 && (
                        <div className="absolute bottom-2 left-2 bg-[#1A1A1A] text-white text-xs px-2 py-1 rounded-full">
                          Main
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-[#999]">
                      {index === 0 ? (
                        <>
                          <Camera className="w-8 h-8 mb-2" />
                          <span className="text-sm">Main Photo</span>
                        </>
                      ) : (
                        <Plus className="w-6 h-6" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-center text-[#999] text-sm">
              Drag and drop or click to upload • JPG, PNG up to 10MB
            </p>

            <div className="flex justify-between">
              <Button
                onClick={prevStep}
                variant="ghost"
                className="text-[#666] hover:text-[#1A1A1A]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={nextStep}
                className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-10 py-7 text-base"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Profile & Prompts */}
        {currentStep === 3 && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h1 className="font-serif text-4xl text-[#1A1A1A] mb-3">Tell your story</h1>
              <p className="text-[#666]">Short prompts that spark conversations</p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
              {/* Professional Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    <Briefcase className="w-4 h-4 inline mr-1" /> Profession *
                  </label>
                  <Input
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    placeholder="e.g., Product Designer"
                    className="h-12 rounded-xl border-[#EEE] focus:border-[#1A1A1A]"
                    data-testid="input-profession"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Company</label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Where you work"
                    className="h-12 rounded-xl border-[#EEE] focus:border-[#1A1A1A]"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" /> City *
                </label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Mumbai, Bangalore"
                  className="h-12 rounded-xl border-[#EEE] focus:border-[#1A1A1A]"
                  data-testid="input-location"
                />
              </div>
            </div>

            {/* Soloist Prompts */}
            <div className="space-y-4">
              <h3 className="font-medium text-[#1A1A1A]">Soloist Prompts</h3>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <label className="block text-sm text-[#C5B358] font-medium mb-2">
                  My next solo escape is to...
                </label>
                <Textarea
                  name="prompt1"
                  value={formData.prompt1}
                  onChange={handleInputChange}
                  placeholder="Japan for cherry blossoms, Iceland for northern lights..."
                  className="min-h-[80px] rounded-xl border-[#EEE] focus:border-[#1A1A1A] resize-none"
                  data-testid="input-prompt1"
                />
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <label className="block text-sm text-[#C5B358] font-medium mb-2">
                  Ask me about my expertise in...
                </label>
                <Textarea
                  name="prompt2"
                  value={formData.prompt2}
                  onChange={handleInputChange}
                  placeholder="AI, photography, wine tasting, hiking trails..."
                  className="min-h-[80px] rounded-xl border-[#EEE] focus:border-[#1A1A1A] resize-none"
                  data-testid="input-prompt2"
                />
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <label className="block text-sm text-[#C5B358] font-medium mb-2">
                  My ideal weekend meetup involves...
                </label>
                <Textarea
                  name="prompt3"
                  value={formData.prompt3}
                  onChange={handleInputChange}
                  placeholder="Coffee and bookstores, hiking at dawn, museum hopping..."
                  className="min-h-[80px] rounded-xl border-[#EEE] focus:border-[#1A1A1A] resize-none"
                  data-testid="input-prompt3"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                onClick={prevStep}
                variant="ghost"
                className="text-[#666] hover:text-[#1A1A1A]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-10 py-7 text-base"
              >
                {loading ? "Creating..." : "Enter Havns"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProfileBuilder;
